import { Box, Button, Stack, Typography } from '@mui/material';
import { youtubeEmbed, type A2Board } from '../lib/entryMedia';

// Shared A2-board + video *preview* rendering, used by both the admin entry
// view (EntryDetail) and the entrant self-service view (SubmitChange) so the
// two present the boards and video identically. Each page keeps its own
// editable URL inputs (admin: edit-mode-gated; entrant: always-on draft) but
// the read-only preview lives here once. Previews always reflect the live
// nomination values, never the in-progress draft. Pure helpers
// (a2BoardsFrom / youtubeEmbed) live in ../lib/entryMedia.

export function A2BoardsPreview({ boards, buttonSx }: { boards: A2Board[]; buttonSx?: object }) {
  if (boards.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No A2 boards submitted.
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
        gap: 2,
      }}
    >
      {boards.map((b) => {
        // Evessio serves A2 boards as whatever the entrant uploaded — ~73% PDF,
        // the rest JPG/PNG. Render inline if image, link out otherwise. The URL
        // has no extension guarantee so we sniff.
        const isImage = /\.(jpe?g|png|gif|webp)(\?|$)/i.test(b.url);
        return (
          <Stack key={b.url} spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {b.label}
            </Typography>
            {isImage ? (
              <Box
                component="a"
                href={b.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'block' }}
              >
                <Box
                  component="img"
                  src={b.url}
                  alt={b.label}
                  sx={{
                    width: '100%',
                    maxHeight: 420,
                    objectFit: 'contain',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.default',
                  }}
                />
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="a"
                href={b.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={buttonSx}
              >
                Open {b.label} (PDF)
              </Button>
            )}
          </Stack>
        );
      })}
    </Box>
  );
}

export function VideoPreview({
  nomination,
  buttonSx,
}: {
  nomination: {
    video_landscape_url?: string | null;
    video_portrait_url?: string | null;
    video_download_url?: string | null;
  };
  buttonSx?: object;
}) {
  const landscape = nomination.video_landscape_url;
  const portrait = nomination.video_portrait_url;
  const download = nomination.video_download_url;
  const hasAny = landscape || portrait || download;
  if (!hasAny) {
    return (
      <Typography variant="body2" color="text.secondary">
        No video submitted.
      </Typography>
    );
  }
  const landscapeEmbed = landscape ? youtubeEmbed(landscape) : null;
  const portraitEmbed = portrait ? youtubeEmbed(portrait) : null;
  return (
    <Stack spacing={2}>
      {landscape && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Landscape
          </Typography>
          {landscapeEmbed ? (
            <Box
              sx={{
                position: 'relative',
                paddingTop: '56.25%',
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                component="iframe"
                src={landscapeEmbed}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                }}
              />
            </Box>
          ) : (
            <Typography variant="body2">
              <a href={landscape} target="_blank" rel="noopener noreferrer">
                {landscape}
              </a>
            </Typography>
          )}
        </Box>
      )}
      {portrait && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Portrait
          </Typography>
          {portraitEmbed ? (
            <Box
              sx={{
                width: { xs: '100%', sm: 320 },
                aspectRatio: '9 / 16',
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                component="iframe"
                src={portraitEmbed}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sx={{ width: '100%', height: '100%', border: 0 }}
              />
            </Box>
          ) : (
            <Typography variant="body2">
              <a href={portrait} target="_blank" rel="noopener noreferrer">
                {portrait}
              </a>
            </Typography>
          )}
        </Box>
      )}
      {download && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Raw file download
          </Typography>
          <Button
            variant="outlined"
            component="a"
            href={download}
            target="_blank"
            rel="noopener noreferrer"
            sx={buttonSx}
          >
            Open download link
          </Button>
        </Box>
      )}
    </Stack>
  );
}
